import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import {
    Booking_Status_Enum,
    Booking_Type_Enum,
    BookingFragment,
    Invoice_Target_Enum,
    Rate_Type_Enum,
    UpdateBookingMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    AgencyOperationsAdminBookingCreateFragment, 
    AgencyOperationsAdminBookingCreateSchema, 
    AgencyOperationsAdminBookingUpdateFragment, 
    AgencyOperationsAdminBookingUpdateSchema 
} from "src/components/agency/operations/administrator/bookings/AgencyOperationsAdminBookingSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { toPgUuidArrayLiteral } from "src/utils/string"
import { getChangedFields } from "src/utils/objects"
import dayjs from "dayjs"

type FormPayloadType = AgencyOperationsAdminBookingCreateFragment

let initialState: FormPayloadType = {
    venue_id: '',
    area_id: '',
    talent_id: '',
    date: '',
    event_start_time: '',
    event_end_time: '',
    set_start_time: '',
    set_end_time: '',
    notes: '',
    rate: '',
    rate_type: Rate_Type_Enum.Hourly,
    rate_margin: '',
    invoice_target: Invoice_Target_Enum.Venue,
    type: Booking_Type_Enum.VenueBooking,
    poc: '',
    recurring: false,
    end_date: '',
    frequency: '',
    interval: 1,
    count: 1,
    by_day: [],
    by_month_day: [],
    rrule: '',
    timezone: '',
};

export const CreateAgencyOperationsAdminBookingDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createBooking, updateItem, deleteItem } = useActions().bookings;
    const { loading, errors } = useAppState().bookings;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: AgencyOperationsAdminBookingCreateFragment) => {

        let {
            talent_id,
            date,
            event_start_time,
            event_end_time,
            venue_id,
            area_id,
            set_start_time,
            set_end_time,
            notes,
            rate,
            rate_type,
            rate_margin,
            invoice_target,
            type,
            poc,
            recurring,
            end_date,
            frequency,
            interval,
            count,
            by_day,
            by_month_day,
            rrule,
            timezone,
        } = payload;

        // check if end_date is set, if not, set it to 9 weeks from today. if end_date is set, but its over 9 weeks from today, set it to 9 weeks from today.
        if (!end_date) {
            end_date = dayjs().add(9, 'weeks').format('YYYY-MM-DD');
        } else {
            if (dayjs(end_date).isAfter(dayjs().add(9, 'weeks'))) {
                end_date = dayjs().add(9, 'weeks').format('YYYY-MM-DD');
            }
        }

        return createBooking({
            talent_id,
            date,
            event_start_time,
            event_end_time,
            venue_id,
            area_id,
            set_start_time,
            set_end_time,
            notes,
            rate,
            rate_type,
            rate_margin,
            invoice_target,
            type,
            poc,
            recurring,
            end_date,
            frequency,
            interval,
            count,
            by_day: toPgUuidArrayLiteral(by_day?.join(',')),
            by_month_day: toPgUuidArrayLiteral(by_month_day?.join(',')),
            rrule,
            timezone,
        });
    }

    return (
        <>
            <FormDrawer<FormPayloadType, BookingFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminBookingCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminBookingAction = () => {
    return (
        <PageDrawerAction<BookingFragment>
            title='Add Booking'
            action={CreateAgencyOperationsAdminBookingDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminBookingUpdateFragment = {
    status: Booking_Status_Enum.Draft,
    venue_id: '',
    area_id: '',
    talent_id: '',
    date: '',
    event_start_time: '',
    event_end_time: '',
    set_start_time: '',
    set_end_time: '',
    notes: '',
    rate: '',
    rate_type: Rate_Type_Enum.Hourly,
    rate_margin: '',
    invoice_target: Invoice_Target_Enum.Venue,
    type: Booking_Type_Enum.VenueBooking,
    poc: ''
};

export const UpdateAgencyOperationsAdminBookingDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {
    
    const { updateItem, deselectItem } = useActions().bookings;
    const { currentItem, loading, errors } = useAppState().bookings;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminBookingUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminBookingUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleDelete = (payload: UpdateBookingMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                status: Booking_Status_Enum.Deleted 
            }
        })      
    }
    
    const handleUpdate = (payload: UpdateBookingMutationVariables) => {
        if (currentItem) {

            let updateTargetId = payload.id

            if ( currentItem.recurring && !currentItem.is_master && currentItem.series) {
                updateTargetId = currentItem.series.master_booking_id
            }

            let changedFields = getChangedFields(currentItem, payload._set)

            return updateItem({
                id: updateTargetId,
                _set: {
                    ...changedFields
                }
            })

        } else {
            return updateItem({
                id: payload.id,
                _set: {
                    ...payload._set
                }
            })
        }        
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminBookingUpdateFragment, BookingFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminBookingUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={handleDelete}
                hideDelete={false}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminBookingAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().bookings;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.bookings.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<BookingFragment>
            title='Edit Booking'
            action={UpdateAgencyOperationsAdminBookingDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminBookingAction = () => {
    return (
        <PageDrawerAction<BookingFragment>
            title='Edit Booking'
            action={UpdateAgencyOperationsAdminBookingDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
            variant="outline"
            color="gray"
        />
    )
}